import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, X } from "lucide-react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { api, imgUrl } from "@/lib/api";
import ImageField from "@/components/admin/ImageField";

const inputCls = "w-full rounded-xl border border-line bg-white px-3.5 py-2.5 text-sm outline-none focus:border-srs";
const EMPTY = { title: "", slug: "", tagline: "", description: "", specsText: "", photos: [] };

export default function ProductsTab() {
  const qc = useQueryClient();
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null); // {form, id|null}

  const load = () => api.get("/products").then(({ data }) => setItems(data));
  useEffect(() => {
    load();
  }, []);

  if (!items) return <p className="font-mono text-xs uppercase tracking-[0.25em] text-subtle">Loading…</p>;

  const move = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    setItems(next);
    await api.post("/admin/reorder", { collection: "products", ids: next.map((p) => p.id) });
    qc.invalidateQueries({ queryKey: ["site"] });
  };

  const del = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    await api.delete(`/admin/products/${p.id}`);
    toast.success("Product deleted.");
    load();
    qc.invalidateQueries({ queryKey: ["site"] });
  };

  const save = async () => {
    const f = editing.form;
    if (!f.title.trim()) return toast.error("Title is required.");
    const payload = {
      title: f.title,
      slug: f.slug,
      tagline: f.tagline,
      description: f.description,
      specs: f.specsText.split("\n").map((x) => x.trim()).filter(Boolean),
      photos: f.photos.filter(Boolean),
    };
    try {
      if (editing.id) await api.put(`/admin/products/${editing.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success("Product saved.");
      setEditing(null);
      load();
      qc.invalidateQueries({ queryKey: ["site"] });
    } catch (e) {
      toast.error("Save failed.");
    }
  };

  const openEdit = (p) =>
    setEditing({
      id: p?.id || null,
      form: p
        ? { title: p.title, slug: p.slug, tagline: p.tagline || "", description: p.description || "", specsText: (p.specs || []).join("\n"), photos: [...(p.photos || [])] }
        : { ...EMPTY },
    });

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <p className="text-sm text-mute">{items.length} categories · order controls the public grid</p>
        <button
          data-testid="admin-product-add"
          onClick={() => openEdit(null)}
          className="flex items-center gap-2 bg-ink px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.18em] text-white hover:bg-srs"
        >
          <Plus size={14} /> Add product
        </button>
      </div>

      <div className="divide-y divide-line rounded-3xl border border-line bg-white shadow-[0_14px_35px_-24px_rgba(13,13,17,0.18)]" data-testid="admin-products-list">
        {items.map((p, i) => (
          <div key={p.id} className="flex items-center gap-4 p-4" data-testid={`admin-product-row-${i}`}>
            <span className="w-7 font-mono text-[11px] text-subtle">{String(i + 1).padStart(2, "0")}</span>
            {p.photos?.[0] ? (
              <img src={imgUrl(p.photos[0])} alt="" className="h-12 w-16 border border-line object-cover" />
            ) : (
              <span className="flex h-12 w-16 items-center justify-center border border-dashed border-line font-mono text-[9px] text-subtle">No img</span>
            )}
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-ink">{p.title}</p>
              <p className="truncate font-mono text-[10px] uppercase tracking-[0.15em] text-subtle">/{p.slug}</p>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => move(i, -1)} disabled={i === 0} className="p-2 text-mute hover:text-ink disabled:opacity-25" aria-label="Move up" data-testid={`admin-product-up-${i}`}><ArrowUp size={15} /></button>
              <button onClick={() => move(i, 1)} disabled={i === items.length - 1} className="p-2 text-mute hover:text-ink disabled:opacity-25" aria-label="Move down" data-testid={`admin-product-down-${i}`}><ArrowDown size={15} /></button>
              <button onClick={() => openEdit(p)} className="p-2 text-mute hover:text-ink" aria-label="Edit" data-testid={`admin-product-edit-${i}`}><Pencil size={15} /></button>
              <button onClick={() => del(p)} className="p-2 text-mute hover:text-srs" aria-label="Delete" data-testid={`admin-product-delete-${i}`}><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink/60 p-4 backdrop-blur-sm" data-testid="admin-product-editor">
          <div className="my-8 w-full max-w-2xl border border-line bg-white p-6 sm:p-8">
            <div className="mb-6 flex items-center justify-between">
              <h2 className="font-display text-xl font-bold tracking-tight">{editing.id ? "Edit product" : "New product"}</h2>
              <button onClick={() => setEditing(null)} className="p-2 text-mute hover:text-ink" aria-label="Close"><X size={18} /></button>
            </div>
            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-mute">Title</label>
                  <input className={inputCls} value={editing.form.title} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, title: e.target.value } })} data-testid="admin-product-title-input" />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-mute">Slug (auto if blank)</label>
                  <input className={inputCls} value={editing.form.slug} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, slug: e.target.value } })} />
                </div>
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-mute">Tagline</label>
                <input className={inputCls} value={editing.form.tagline} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, tagline: e.target.value } })} />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-mute">Description</label>
                <textarea rows={3} className={inputCls} value={editing.form.description} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, description: e.target.value } })} />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-mute">Specs (one per line)</label>
                <textarea rows={5} className={`${inputCls} font-mono text-xs`} value={editing.form.specsText} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, specsText: e.target.value } })} />
              </div>
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <label className="text-xs font-semibold uppercase tracking-wider text-mute">Photos</label>
                  <button type="button" onClick={() => setEditing({ ...editing, form: { ...editing.form, photos: [...editing.form.photos, ""] } })} className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-srs">
                    <Plus size={12} /> Add photo
                  </button>
                </div>
                <div className="space-y-3">
                  {editing.form.photos.map((ph, i) => (
                    <ImageField
                      key={i}
                      value={ph}
                      onChange={(v) => setEditing({ ...editing, form: { ...editing.form, photos: editing.form.photos.map((x, j) => (j === i ? v : x)) } })}
                      testid={`admin-product-photo-${i}`}
                    />
                  ))}
                  {!editing.form.photos.length && <p className="text-xs text-subtle">No photos yet — add one.</p>}
                </div>
              </div>
            </div>
            <div className="mt-8 flex justify-end gap-3">
              <button onClick={() => setEditing(null)} className="border border-line px-6 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-mute hover:text-ink">Cancel</button>
              <button onClick={save} className="bg-srs px-6 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-white hover:bg-srs-dark" data-testid="admin-product-save-button">
                Save product
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
